import cn from 'classnames';
import React, {
    ButtonHTMLAttributes,
    forwardRef,
    HTMLAttributes,
    MouseEvent,
    ReactNode,
} from 'react';
import { Button } from '../button/button';
import { Icon, IconProps } from '../icon/icon';

export interface NotificationProps extends HTMLAttributes<HTMLDivElement> {
    /**
     * If not provided a 'bar' type will be generated. Other variants are 'banner', 'text'
     */
    variant?: 'banner' | 'text';
    type?: 'neutral' | 'success' | 'warning' | 'error';
    icon?: string | IconProps;
    /**
     * onClick handler of close button
     */
    onCloseClick?: (event: MouseEvent<HTMLButtonElement>) => void;
    /**
     * Props that will be forwarded to close button.
     * Note, providing onClick will override onCloseClick
     */
    closeButton?: ButtonHTMLAttributes<HTMLButtonElement>;
    /**
     * replaces inner div of notification
     */
    content?: ReactNode;
    /**
     * gets inserted into the inner div of the notification
     */
    children?: ReactNode;
}

export const Notification = forwardRef<HTMLDivElement, NotificationProps>(
    (props, ref) => {
        const {
            variant,
            content,
            type = 'neutral',
            icon,
            id,
            className,
            children,
            onCloseClick,
            closeButton,
            ...rest
        } = props;
        const notificationLabelId = id
            ? `${id}-label`
            : 'notification-label-id';

        const notificationClass = cn(
            'a-notification',
            {
                [`a-notification--${variant}`]: variant,
                [`-${type}`]: type,
            },
            className,
        );

        const iconProps =
            typeof icon === 'string' ? { iconName: icon } : { ...icon };

        return (
            <div
                className={notificationClass}
                id={id}
                role="alert"
                aria-labelledby={notificationLabelId}
                ref={ref}
                {...rest}
            >
                {/* if not neutral variant, a icon is mandatory and a uiIcon */}
                {type !== 'neutral' && (
                    <Icon iconName={`alert-${type}`} isUiIcon />
                )}
                {/* optional icon if type is neutral */}
                {type === 'neutral' && icon && <Icon {...iconProps} />}
                {content || (
                    <div
                        id={notificationLabelId}
                        className="a-notification__content"
                    >
                        {children}
                    </div>
                )}
                {variant === 'banner' && (
                    <Button
                        action="close"
                        mode="integrated"
                        icon={{ iconName: 'close', isUiIcon: true }}
                        aria-label="close banner"
                        onClick={onCloseClick}
                        {...closeButton}
                    />
                )}
            </div>
        );
    },
);
Notification.displayName = 'Notification';
